import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Dumbbell, GraduationCap, CalendarDays,
  Megaphone, Inbox, LogOut, ExternalLink, Menu, X,
} from "lucide-react";
import { useAuth } from "./AuthContext";

const SECTIONS = [
  { key: "overview", label: "Özet", icon: LayoutDashboard },
  { key: "branches", label: "Branşlar", icon: Dumbbell },
  { key: "courses", label: "Kurslar", icon: GraduationCap },
  { key: "events", label: "Etkinlikler", icon: CalendarDays },
  { key: "announcements", label: "Duyurular", icon: Megaphone },
  { key: "submissions", label: "Form Gönderimleri", icon: Inbox },
];

export default function AdminLayout({ active, onChange, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* SIDEBAR (desktop) */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-[hsl(218_55%_5%)] sticky top-0 h-screen">
        <SidebarContent active={active} onChange={onChange} user={user} onLogout={handleLogout} />
      </aside>

      {/* MOBILE TOPBAR */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-background/90 backdrop-blur-xl border-b border-border flex items-center justify-between px-4">
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/assets/fcs-logo.png" alt="FCS" className="h-8 w-8" />
          <span className="font-display font-black uppercase tracking-wider text-sm">Admin</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-border bg-secondary"
          data-testid="admin-menu-open"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/95 backdrop-blur-xl" data-testid="admin-mobile-menu">
          <div className="flex items-center justify-between px-4 h-14 border-b border-border">
            <span className="font-display font-black uppercase tracking-wider">Menü</span>
            <button type="button" onClick={() => setOpen(false)} className="h-10 w-10 rounded-md border border-border" data-testid="admin-menu-close">
              <X className="h-5 w-5 mx-auto" />
            </button>
          </div>
          <div className="p-4">
            <SidebarContent
              active={active}
              onChange={(k) => { onChange(k); setOpen(false); }}
              user={user}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="px-5 md:px-8 py-7 md:py-10 max-w-6xl">
          {children}
        </div>
      </main>
    </div>
  );
}

function SidebarContent({ active, onChange, user, onLogout }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-border">
        <Link to="/admin" className="flex items-center gap-3">
          <img src="/assets/fcs-logo.png" alt="FCS" className="h-11 w-11" />
          <div>
            <div className="font-display text-base font-black uppercase tracking-wider">Yönetim</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">FCS Admin</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = active === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              data-testid={`admin-nav-${s.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md font-display text-sm font-bold uppercase tracking-wider transition-all ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              }`}
            >
              <Icon className="h-4 w-4" />
              {s.label}
            </button>
          );
        })}
        <Link
          to="/"
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md font-display text-sm font-bold uppercase tracking-wider text-muted-foreground hover:bg-secondary"
          data-testid="admin-view-site"
        >
          <ExternalLink className="h-4 w-4" />
          Siteyi Görüntüle
        </Link>
      </nav>

      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 rounded-md bg-secondary mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Giriş Yapan</div>
          <div className="text-sm font-display font-bold truncate">{user?.email}</div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md font-display text-sm font-bold uppercase tracking-wider text-destructive hover:bg-destructive/10 border border-destructive/30"
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4" /> Çıkış Yap
        </button>
      </div>
    </div>
  );
}
